import { TourActions } from './constants'
import { getIndex, getStopTrailPoint } from 'aria-viewer/utils/functions'
import { Map, List } from 'immutable'
import QueryParamUtils from 'aria-viewer/utils/queryParamUtils'

const INITIAL_STATE = Map({
    tourTitle: '',
    fontFace: null,
    isExperienceOffline: false,
    isAutoPlayBlocked: false,
    isAutoPlayEnabled: false,
    experienceUpdated: null,
    client: {
        isClientApp: false,
        isAppAvailable: false,
        hasShownPrompt: false,
        image: '',
    },
    isClient: null,
    instid: null,
    currentPanoramaParams: Map({}),
    tours: Map({
        current: null,
        list: [],
    }),
    locations: Map({
        current: 0,
        list: [],
    }),
    stops: Map({
        current: null,
        list: List([]),
        cache: {},
        previous: 0,
        visited: Map({}),
        visitedCount: 0,
        isStopIntroPlayed: Map({}),
    }),
    trails: Map({
        current: 0,
        list: [],
        cache: {},
    }),
    points: Map({
        current: 0,
        list: [],
    }),
    actionButtons: Map({
        initialized: false,
        actions: [],
        visible: false,
    }),
    loading: Map({
        tour: false,
        location: false,
        media: 0,
    }),
    altMainMedia: null,
    toggleDescriptionVisible: false,
    registration: Map({
        enabled: null,
        welcomePrompt: false,
        // hardwire to false for iframe embedding with an iframe detect here
        autoLaunch: undefined,
    }),
    mainMedia: Map({
        initialized: false,
        stalling: false,
        loadingStatus: null,
        mediaType: null,
        width: null,
        height: null,
        photoPlayer: {
            left: null,
            top: null,
            bottom: null,
            width: null,
            height: null,
            x: null,
            y: null,
        },
    }),
    platform: null,
    originalPlatform: null,
    loadSingleStopOnly: false,
    tourHintButton: true,
    skippedAltMainMedia: false,
    isSkipButtonVisible: true, //this is referring to the skip button on the altmainmedia shown by clicking trigger
    welcomePrompt: null, //this indicates the welcome prompt without registration
    landscapePrompt: null,
    fromVR: null, //indicates the tour was initiated from vrapp
    isWindowFullScreen: false,
    isEmbedExpanded: false,
    isTourInitializedComplete: false,
})

export default (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case TourActions.TOUR_INITIALIZED_COMPLETE:
            return state.set('isTourInitializedComplete', action.isTourInitializedComplete)
        case TourActions.SET_TOUR_EMBED_EXPANDED:
            return state.set('isEmbedExpanded', action.isExpanded)
        case TourActions.SET_TOUR_TITLE:
            return state.set('tourTitle', action.title)
        case TourActions.SET_FONT_FACE:
            return state.set('fontFace', action.fontFace)
        case TourActions.SET_IS_EXPERIENCE_OFFLINE:
            return state.set('isExperienceOffline', action.isExperienceOffline)
        case TourActions.SET_EXPERIENCE_UPDATED:
            return state.set('experienceUpdated', action.experienceUpdated)
        case TourActions.SET_CLIENT:
            return state.set('client', action.value)
        case TourActions.SET_LOCATION:
            return state.withMutations(mState => {
                return mState
                    .setIn(['stops', 'visitedCount'], 0)
                    .setIn(['stops', 'visited'], Map({}))
                    .setIn(['locations', 'current'], action.current)
            })
        case TourActions.GO_TO_POINT: {
            const { stops_current, trails_current, points_current } = getStopTrailPoint(state, action.point)

            state = state.setIn(['stops', 'visited', stops_current], true)

            return setResource(
                setResource(setResource(state, 'stops', { current: stops_current }), 'trails', {
                    current: trails_current,
                }),
                'points',
                { current: points_current }
            )
        }
        case TourActions.MAIN_MEDIA_INITIALIZED:
            return state.withMutations(mState => {
                const initialWidth = INITIAL_STATE.getIn(['mainMedia', 'width'])
                const initialHeight = INITIAL_STATE.getIn(['mainMedia', 'height'])
                const initialPhotoPlayer = INITIAL_STATE.getIn(['mainMedia', 'photoPlayer'])

                return mState
                    .setIn(['mainMedia', 'initialized'], action.initialized)
                    .setIn(['mainMedia', 'width'], initialWidth)
                    .setIn(['mainMedia', 'height'], initialHeight)
                    .setIn(['mainMedia', 'photoPlayer'], initialPhotoPlayer)
            })
        case TourActions.MAIN_MEDIA_LOADING_STATUS:
            return state.setIn(['mainMedia', 'loadingStatus'], action.loadingStatus)
        case TourActions.MAIN_MEDIA_TYPE:
            return state.setIn(['mainMedia', 'mediaType'], action.mediaType)
        case TourActions.MAIN_MEDIA_IS_STALLING:
            return state.setIn(['mainMedia', 'stalling'], action.isStalling)
        case TourActions.LOADING:
            return state.set('loading', state.get('loading').merge(action.value))
        case TourActions.ERROR:
            return state.set('error', action.value)
        case TourActions.SET_INSTITUTION:
            return state.merge({
                instid: action.instid,
                isClient: action.isClient,
            })
        case TourActions.SET_LOCATIONS:
            return state.set('locations', state.get('locations').set('list', action.locations))
        case TourActions.SET_TOURS:
            return state.set('tours', state.get('locations').set('list', action.tours))
        case TourActions.SET_TOUR:
            return state.withMutations(mState => {
                let tourIndex = mState
                    .get('tours')
                    .get('list')
                    .findIndex(tour => tour.tourid == action.current && tour.loc_id == action.tour.loc_id)
                return mState.merge({
                    tours: mState
                        .get('tours')
                        .set('current', action.current)
                        .set(
                            'list',
                            List(mState.getIn(['tours', 'list']))
                                .set(tourIndex, action.tour)
                                .toArray()
                        ),
                    stops: mState.get('stops').set('list', List(mState.get('stops').get('cache')[action.current])),
                    trails: mState.get('trails').set('list', mState.get('trails').get('cache')[action.current]),
                })
            })
        case TourActions.SET_STOPS_CACHE:
            return setResourceCache(state, 'stops', action)
        case TourActions.SET_STOP:
            return state.withMutations(mState => {
                const visitedStopsCount = mState.getIn(['stops', 'visitedCount'])
                const { trails_current, points_current } = getIndex(action.current)(mState)

                mState = setResource(mState, 'points', { current: points_current })
                mState = setResource(mState, 'trails', { current: trails_current })
                mState = setResource(mState, 'stops', action)

                return mState
                    .setIn(['stops', 'visited', action.current], true)
                    .setIn(['stops', 'visitedCount'], visitedStopsCount + 1)
            })
        case TourActions.SET_STOP_VISITED:
            return state.setIn(['stops', 'visited', action.stopid], action.visited)
        case TourActions.CLEAR_VISITED_STOPS:
            return state.setIn(['stops', 'visited'], INITIAL_STATE.getIn(['stops', 'visited']))
        case TourActions.SET_TRAILS_CACHE:
            return setResourceCache(state, 'trails', action)
        case TourActions.SET_TRAIL:
            return setResource(state, 'trails', action)
        case TourActions.SET_POINTS:
            return state.set('points', state.get('points').set('list', action.points))
        case TourActions.SET_POINT:
            return setResource(state, 'points', action)
        case TourActions.SET_ACTION_BUTTONS:
            return state.set(
                'actionButtons',
                state.get('actionButtons').merge(
                    Map({
                        ...action.actionButtons,
                        initialized: true,
                    })
                )
            )
        case TourActions.SET_PANORAMA_PARAMS:
            const { size, panoType } = action
            return state.set(
                'currentPanoramaParams',
                Map({
                    size,
                    panoType,
                })
            )
        case TourActions.SET_REGISTRATION_AUTOLAUNCH:
            return state.setIn(
                ['registration', 'autoLaunch'],
                QueryParamUtils.useResponsiveUI() ? false : action.autoLaunch
            )
        case TourActions.SET_REGISTRATION_ENABLED:
            return state.setIn(['registration', 'enabled'], action.enabled)
        case TourActions.SET_REGISTRATION_WELCOME_PROMPT:
            return state.setIn(['registration', 'welcomePrompt'], action.prompt)
        case TourActions.SET_WELCOME_PROMPT:
            return state.set('welcomePrompt', action.prompt)
        case TourActions.SET_LANDSCAPE_PROMPT:
            return state.set('landscapePrompt', action.prompt)
        case TourActions.SET_PLATFORM:
            return state.set('platform', action.platform)
        case TourActions.SET_ORIGINAL_PLATFORM:
            return state.set('originalPlatform', action.original_platform)
        case TourActions.SET_DISPLAY_SINGLE_STOP:
            return state.set('loadSingleStopOnly', action.loadSingleStopOnly)
        case TourActions.SHOW_ALT_MEDIA:
            return state.set('altMainMedia', action.media)
        case TourActions.SHOW_DESCRIPTION:
            return state.set('toggleDescriptionVisible', action.visible)
        case TourActions.TOUR_NAV_HINT_BUTTON:
            return state.set('tourHintButton', action.visible)
        case TourActions.SKIPPED_ALT_MAIN_MEDIA:
            return state.set('skippedAltMainMedia', action.skipped)
        case TourActions.SHOW_SKIP:
            return state.set('isSkipButtonVisible', action.visible)
        case TourActions.IS_AUTO_PLAY_BLOCKED:
            return state.set(
                'isAutoPlayBlocked',
                //state.get("isAutoPlayBlocked") + (action.isAutoPlayBlocked ? 1 : -1)
                // TODO : testing if this works; remove above if so
                action.isAutoPlayBlocked
            )
        case TourActions.IS_AUTO_PLAY_ENABLED:
            return state.set('isAutoPlayEnabled', action.isAutoPlayEnabled)
        case TourActions.FROM_VR:
            return state.set('fromVR', action.fromVR)
        case TourActions.IS_WINDOW_FULLSCREEN:
            return state.set('isWindowFullScreen', action.isWindowFullScreen)
        case TourActions.SET_IS_STOP_INTRO_PLAYED:
            return state.setIn(['stops', 'isStopIntroPlayed', action.currentStopId], true)
        case TourActions.SET_MAIN_MEDIA_DIMENSION:
            return state.withMutations(mState => {
                const { width, height } = action.dimension
                return mState.setIn(['mainMedia', 'width'], width).setIn(['mainMedia', 'height'], height)
            })
        case TourActions.SET_MAIN_MEDIA_PHOTO_PLAYER_BOUNDING_RECT:
            return state.setIn(['mainMedia', 'photoPlayer'], action.boundingRect)
        case TourActions.RESET_TOUR_DATA:
            // Reset all key/value pairs except locations, tours, instid, and isClient since
            // these values are only called once via setLocations action (via tourActions)
            return INITIAL_STATE.merge({
                locations: state.get('locations'),
                tours: state.get('tours'),
                instid: state.get('instid'),
                isClient: state.get('isClient'),
                isAutoPlayEnabled: state.get('isAutoPlayEnabled'),
                experienceUpdated: state.get('experienceUpdated'),
                actionButtons: INITIAL_STATE.get('actionButtons').set(
                    'visible',
                    state.getIn(['actionButtons', 'visible'])
                ),
                isTourInitializedComplete: state.get('isTourInitializedComplete'),
            })
        default:
            return state
    }
}

const setResource = (state, name, action) => {
    return state.set(name, state.get(name).set('current', action.current))
}
const setResourceCache = (state, name, action) => {
    return state.set(name, state.get(name).set('cache', action[name] || {}))
}
